import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "@/App.css";
import shared from "@/seo/shared";
import { useSeo } from "@/seo/useSeo";
import ServicePage from "@/pages/ServicePage";
import LocationPage from "@/pages/LocationPage";
import { AreaPage, ContactPage } from "@/pages/AreaPage";
import { AreaSection } from "@/components/site/AreaSection";
import { Toaster } from "@/components/ui/sonner";
import { Nav } from "@/components/site/Nav";
import { Hero } from "@/components/site/Hero";
import { Marquee } from "@/components/site/Marquee";
import { Services } from "@/components/site/Services";
import { About } from "@/components/site/About";
import { Gallery } from "@/components/site/Gallery";
import { Process } from "@/components/site/Process";
import { Faq } from "@/components/site/Faq";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { CookieBanner, StickyBar, WhatsAppFab } from "@/components/site/Overlays";
import { ExitOffer } from "@/components/site/ExitOffer";
import LegalPage from "@/components/site/LegalPage";
import { scrollToId } from "@/components/site/scroll";

const CONSENT_KEY = "cb-consent";

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let id = requestAnimationFrame(function raf(t) {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

const HOME_LD = shared.businessLd();

const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Dynamic = (props) => {
  const { slug } = useParams();
  const s = shared.services.find((x) => x.slug === slug);
  if (s) return <ServicePage key={slug} s={s} {...props} />;
  const l = shared.locations.find((x) => x.slug === slug);
  if (l) return <LocationPage key={slug} l={l} {...props} />;
  return <Home {...props} />;
};

const Home = ({ consent, onCookies }) => {
  const { hash } = useLocation();
  useSeo({ ...shared.HOME, ld: HOME_LD });
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => scrollToId(hash.slice(1)), 400);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <>
      <a href="#leistungen" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-white focus:p-3 focus:text-ink">Zum Inhalt springen</a>
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <Services />
        <About />
        <Gallery />
        <Process />
        <AreaSection />
        <Faq />
        <Contact consent={consent} />
      </main>
      <Footer onCookies={onCookies} />
      <ExitOffer />
    </>
  );
};

function App() {
  const [consent, setConsentState] = useState(() => localStorage.getItem(CONSENT_KEY));
  useLenis();

  const setConsent = (v) => {
    if (v) localStorage.setItem(CONSENT_KEY, v);
    else localStorage.removeItem(CONSENT_KEY);
    setConsentState(v);
  };
  const resetConsent = () => setConsent(null);

  return (
    <BrowserRouter>
      <ScrollTop />
      <Routes>
        <Route path="/" element={<Home consent={consent} onCookies={resetConsent} />} />
        <Route path="/einsatzgebiet" element={<AreaPage consent={consent} onCookies={resetConsent} />} />
        <Route path="/kontakt" element={<ContactPage consent={consent} onCookies={resetConsent} />} />
        <Route path="/impressum" element={<LegalPage type="impressum" onCookies={resetConsent} />} />
        <Route path="/datenschutz" element={<LegalPage type="datenschutz" onCookies={resetConsent} />} />
        <Route path="/:slug" element={<Dynamic consent={consent} onCookies={resetConsent} />} />
        <Route path="*" element={<Home consent={consent} onCookies={resetConsent} />} />
      </Routes>
      <CookieBanner consent={consent} setConsent={setConsent} />
      <StickyBar />
      <WhatsAppFab />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
